import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Database } from 'lucide-react';
import { AnimatedStat } from '@/components/ui/AnimatedStat';

/**
 * LiveWarehouseTeaser — public, anonymised marketing strip.
 * Shows rounded aggregate counts from the live warehouse (no per-business data)
 * and nudges visitors into the /demo sandbox to lift conversion.
 */
export default function LiveWarehouseTeaser() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/platform-stats/warehouse-teaser`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d && d.success) setData(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const stats = data
    ? [
        { label: 'Australian businesses tracked', value: data.businessesTrackedDisplay },
        { label: 'Industries covered', value: 'All' },
        { label: 'Coverage', value: 'Australia-wide' },
        ...(data.chainsMapped > 0
          ? [{ label: 'Multi-site chains mapped', value: data.chainsMappedDisplay }]
          : [{ label: 'Government & industry sources', value: `${data.dataSources}+` }]),
      ]
    : [];

  if (!data) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.6 }}
      className="mt-8 md:mt-12 mx-auto max-w-4xl"
      data-testid="live-warehouse-teaser"
    >
      <div className="rounded-2xl border border-slate-200/80 bg-white/60 backdrop-blur-xl shadow-lg shadow-slate-900/5 px-5 py-4 md:px-8 md:py-6">
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <span className="text-xs md:text-sm font-medium text-slate-500 tracking-wide uppercase">
            Live intelligence warehouse
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((s, i) => (
            <div key={i} className="text-center" data-testid={`teaser-stat-${i}`}>
              <AnimatedStat value={s.value} className="text-2xl md:text-4xl font-display font-bold text-slate-900 tabular-nums block" />
              <div className="mt-1 text-[11px] md:text-xs text-slate-500 leading-tight">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-center">
          <Link
            to="/demo"
            data-testid="teaser-demo-link"
            className="group inline-flex items-center gap-2 text-sm md:text-base font-medium text-[#007AFF] hover:text-slate-900 transition-colors"
          >
            <Database className="w-4 h-4" />
            <span>Explore it live in the interactive demo</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
